import { AmlogicService, type V2ReacquiredDeviceEvent, type V2ReacquiredDeviceHandler, type V3ReacquiredDeviceEvent, type V3ReacquiredDeviceHandler } from "../AmlogicService";
import { isAmlogicAutoConnectDevice, webUsbFilters } from "../../usb/UsbDeviceMatcher";
import { LEGACY_AMLOGIC_PRODUCT_IDS } from "../../../config/amlogic-legacy";
import { operationLogger } from "../../operations/OperationLogger";
import type { AmlogicDeviceSession, AmlogicGeneration, AmlogicSessionService, AmlogicSessionStatus, SessionLogEntry } from "./AmlogicDeviceSession";
import { V2ReacquireCoordinator } from "./V2ReacquireCoordinator";
import type { DeviceInfo, OperationProgress } from "../../types";

function detectGeneration(device: USBDevice): AmlogicGeneration {
  return (LEGACY_AMLOGIC_PRODUCT_IDS as readonly number[]).includes(device.productId)
    ? "legacy-v2"
    : "adnl-v3";
}

// Mutable internal representation — never exposed directly to React.
type MutableSession = {
  id: string;
  label: string;
  generation: AmlogicGeneration;
  service: AmlogicSessionService;
  usbDevice?: USBDevice;
  connectionEpoch: number;
  info?: DeviceInfo;
  status: AmlogicSessionStatus;
  progress?: OperationProgress;
  error?: string;
  logs: SessionLogEntry[];
  activeJobId?: string;
};

export class AmlogicSessionManager {
  private readonly _sessions = new Map<string, MutableSession>();
  /** USBDevice object identity → session id for currently-live handles. */
  private readonly _deviceMap = new WeakMap<USBDevice, string>();
  private readonly _listeners = new Set<() => void>();
  private _v2Counter = 0;
  private _v3Counter = 0;
  // Cached snapshot rebuilt on every mutation; lets React compare by reference.
  private _snapshot: AmlogicDeviceSession[] = [];
  private _autoIdentify = false;
  /** Shared across every V2 session so full-package flashes serialize globally (Milestone 5). */
  private readonly _reacquireCoordinator: V2ReacquireCoordinator;
  private readonly _serviceFactory: (sessionId: string, onLegacyReacquired: V2ReacquiredDeviceHandler, onAdnlReacquired: V3ReacquiredDeviceHandler) => AmlogicSessionService;

  constructor(serviceFactory?: (sessionId: string, onLegacyReacquired: V2ReacquiredDeviceHandler, onAdnlReacquired: V3ReacquiredDeviceHandler) => AmlogicSessionService) {
    this._reacquireCoordinator = new V2ReacquireCoordinator((device) => this._deviceMap.has(device));
    this._serviceFactory = serviceFactory ?? ((sessionId, onLegacyReacquired, onAdnlReacquired) => (
      new AmlogicService(this._reacquireCoordinator, onLegacyReacquired, onAdnlReacquired, (device) => {
        const owner = this._deviceMap.get(device);
        return Boolean(owner && owner !== sessionId);
      })
    ));
  }

  get reacquireCoordinator(): V2ReacquireCoordinator {
    return this._reacquireCoordinator;
  }

  // ── React integration ────────────────────────────────────────────────────

  subscribe(listener: () => void): () => void {
    this._listeners.add(listener);
    return () => { this._listeners.delete(listener); };
  }

  getSnapshot(): AmlogicDeviceSession[] {
    return this._snapshot;
  }

  // ── Internal helpers ─────────────────────────────────────────────────────

  private _notify(): void {
    this._snapshot = Array.from(this._sessions.values()) as AmlogicDeviceSession[];
    for (const fn of this._listeners) fn();
  }

  private _patch(id: string, patch: Partial<Omit<MutableSession, "id" | "service" | "generation" | "label">>): void {
    const s = this._sessions.get(id);
    if (!s) return;
    Object.assign(s, patch);
    this._notify();
  }

  private _pushLog(id: string, message: string, context?: { batchId?: string; jobId?: string }): void {
    const s = this._sessions.get(id);
    if (!s) return;
    const entry: SessionLogEntry = { timestamp: new Date().toISOString(), message, ...context };
    s.logs = [...s.logs.slice(-99), entry];
    this._notify();
  }

  private _makeLabel(gen: AmlogicGeneration): string {
    if (gen === "legacy-v2") {
      this._v2Counter += 1;
      return `V2-${String(this._v2Counter).padStart(2, "0")}`;
    }
    this._v3Counter += 1;
    return `V3-${String(this._v3Counter).padStart(2, "0")}`;
  }

  private _createSession(device: USBDevice): MutableSession {
    const generation = detectGeneration(device);
    const id = crypto.randomUUID();
    const onLegacyReacquired: V2ReacquiredDeviceHandler = (event) => this._bindReacquiredDevice(id, event);
    const onAdnlReacquired: V3ReacquiredDeviceHandler = (event) => this._bindAdnlReacquiredDevice(id, event);
    const session: MutableSession = {
      id,
      label: this._makeLabel(generation),
      generation,
      service: this._serviceFactory(id, onLegacyReacquired, onAdnlReacquired),
      usbDevice: device,
      connectionEpoch: 0,
      status: "discovered",
      logs: [],
    };
    this._sessions.set(session.id, session);
    this._deviceMap.set(device, session.id);
    this._notify();
    return session;
  }

  private _isPendingV2ReacquireDevice(device: USBDevice): boolean {
    if (detectGeneration(device) !== "legacy-v2") return false;
    return Array.from(this._sessions.values()).some(
      (session) => session.generation === "legacy-v2" && session.service.isReconnecting,
    );
  }

  private _isPendingV3ReacquireDevice(device: USBDevice): boolean {
    if (detectGeneration(device) !== "adnl-v3") return false;
    return Array.from(this._sessions.values()).some(
      (session) => session.generation === "adnl-v3" && session.service.isReconnecting,
    );
  }

  /** Atomically transfer a post-reset C003 handle back to its existing V2 session. */
  private _bindReacquiredDevice(sessionId: string, event: V2ReacquiredDeviceEvent): number {
    const session = this._sessions.get(sessionId);
    if (!session) throw new Error(`Amlogic session ${sessionId} no longer exists.`);
    if (session.generation !== "legacy-v2") throw new Error("Only Legacy V2 sessions may bind a C003 reacquire handle.");

    const existingOwner = this._deviceMap.get(event.usbDevice);
    if (existingOwner && existingOwner !== sessionId) {
      throw new Error(`The reacquired USB handle is already owned by session ${existingOwner}.`);
    }

    if (session.usbDevice && session.usbDevice !== event.usbDevice) {
      this._deviceMap.delete(session.usbDevice);
    }
    session.usbDevice = event.usbDevice;
    session.connectionEpoch += 1;
    session.error = undefined;
    this._deviceMap.set(event.usbDevice, sessionId);
    this._pushLog(sessionId, `Legacy V2 reacquire · stage ${event.stageMinor} · epoch ${session.connectionEpoch}`);
    operationLogger.info("AMLOGIC", "Legacy v2 ownership", `Bound stage ${event.stageMinor} to the existing session.`, {
      sessionId,
      deviceLabel: session.label,
      connectionEpoch: session.connectionEpoch,
      jobId: session.activeJobId,
    });
    return session.connectionEpoch;
  }

  /** Keep every BL1/BL2/BL2E/U-Boot handle on the V3 session that started the job. */
  private _bindAdnlReacquiredDevice(sessionId: string, event: V3ReacquiredDeviceEvent): number {
    const session = this._sessions.get(sessionId);
    if (!session) throw new Error(`Amlogic session ${sessionId} no longer exists.`);
    if (session.generation !== "adnl-v3") throw new Error("Only ADNL V3 sessions may bind an ADNL reacquire handle.");

    const existingOwner = this._deviceMap.get(event.usbDevice);
    if (existingOwner && existingOwner !== sessionId) {
      throw new Error(`The reacquired ADNL handle is already owned by session ${existingOwner}.`);
    }
    if (session.usbDevice && session.usbDevice !== event.usbDevice) {
      this._deviceMap.delete(session.usbDevice);
    }
    session.usbDevice = event.usbDevice;
    session.connectionEpoch += 1;
    session.error = undefined;
    this._deviceMap.set(event.usbDevice, sessionId);
    this._pushLog(sessionId, `ADNL V3 re-enumerate · giữ session ${session.label} · epoch ${session.connectionEpoch}`);
    operationLogger.info("AMLOGIC", "ADNL v3 ownership", "Bound the re-enumerated ADNL handle to the active session.", {
      sessionId,
      deviceLabel: session.label,
      connectionEpoch: session.connectionEpoch,
      jobId: session.activeJobId,
    });
    return session.connectionEpoch;
  }

  // ── USB event handlers (called from App.tsx useEffect) ───────────────────

  async handleUsbConnect(device: USBDevice): Promise<void> {
    if (!isAmlogicAutoConnectDevice(device)) return;
    if (this._deviceMap.has(device)) return; // already tracked by this handle
    // The active V2 flash owns post-reset candidate discovery. Let its probe
    // bind the handle instead of creating a duplicate session from this event.
    if (this._isPendingV2ReacquireDevice(device)) return;
    // BL1/BL2/BL2E/U-Boot are successive handles of the same V3 job. The
    // service polling loop binds the new handle atomically to its old session.
    if (this._isPendingV3ReacquireDevice(device)) return;

    // Try to re-associate with a disconnected session using serial number.
    if (device.serialNumber) {
      for (const [id, s] of this._sessions) {
        if (
          s.status === "disconnected" &&
          s.usbDevice?.vendorId === device.vendorId &&
          s.usbDevice.productId === device.productId &&
          s.usbDevice.serialNumber === device.serialNumber
        ) {
          this._deviceMap.set(device, id);
          this._patch(id, {
            usbDevice: device,
            connectionEpoch: s.connectionEpoch + 1,
            status: "discovered",
            error: undefined,
          });
          this._pushLog(id, "Thiết bị kết nối lại (serial match).");
          await this._connectSession(id);
          return;
        }
      }
    }

    const session = this._createSession(device);
    await this._connectSession(session.id);
  }

  handleUsbDisconnect(device: USBDevice): void {
    const sessionId = this._deviceMap.get(device);
    if (!sessionId) return;
    const s = this._sessions.get(sessionId);
    if (!s) return;
    // ADNL BL1→U-Boot chain intentionally disconnects; keep session alive.
    if (s.service.isReconnecting) return;
    this._patch(sessionId, {
      status: "disconnected",
      activeJobId: undefined,
      progress: undefined,
    });
    this._pushLog(sessionId, "Thiết bị đã ngắt kết nối.");
  }

  // ── Public API ────────────────────────────────────────────────────────────

  async scanAuthorizedDevices(): Promise<void> {
    const usb = navigator.usb;
    if (!usb) return;
    let devices: USBDevice[];
    try {
      devices = await usb.getDevices();
    } catch {
      return;
    }

    for (const device of devices) {
      if (!isAmlogicAutoConnectDevice(device)) continue;
      if (this._deviceMap.has(device)) continue;
      if (this._isPendingV2ReacquireDevice(device)) continue;
      if (this._isPendingV3ReacquireDevice(device)) continue;

      let reassociated = false;
      if (device.serialNumber) {
        for (const [id, s] of this._sessions) {
          if (
            s.status === "disconnected" &&
            s.usbDevice?.vendorId === device.vendorId &&
            s.usbDevice.productId === device.productId &&
            s.usbDevice.serialNumber === device.serialNumber
          ) {
            this._deviceMap.set(device, id);
            this._patch(id, {
              usbDevice: device,
              connectionEpoch: s.connectionEpoch + 1,
              status: "discovered",
              error: undefined,
            });
            await this._connectSession(id);
            reassociated = true;
            break;
          }
        }
      }
      if (!reassociated) {
        const session = this._createSession(device);
        await this._connectSession(session.id);
      }
    }

    // Mark sessions whose physical device disappeared from the authorized list.
    for (const [id, s] of this._sessions) {
      if (!s.usbDevice || s.status === "disconnected") continue;
      if (s.service.isReconnecting) continue;
      if (!devices.some((d) => d === s.usbDevice)) {
        this._patch(id, { status: "disconnected", activeJobId: undefined, progress: undefined });
        this._pushLog(id, "Thiết bị không còn trong danh sách USB.");
      }
    }
  }

  async requestAndAddDevice(): Promise<string> {
    const usb = navigator.usb;
    if (!usb) throw new Error("WebUSB không được hỗ trợ trong trình duyệt này.");
    const device = await usb.requestDevice({ filters: webUsbFilters("AMLOGIC") });

    if (this._deviceMap.has(device)) {
      const existingId = this._deviceMap.get(device)!;
      const existing = this._sessions.get(existingId);
      if (existing?.status === "disconnected") {
        this._patch(existingId, { status: "discovered", error: undefined });
        await this._connectSession(existingId);
      }
      return existingId;
    }

    const session = this._createSession(device);
    await this._connectSession(session.id);
    return session.id;
  }

  async connectSession(sessionId: string): Promise<void> {
    return this._connectSession(sessionId);
  }

  private async _connectSession(sessionId: string): Promise<void> {
    const s = this._sessions.get(sessionId);
    if (!s?.usbDevice) return;
    // Guard against re-entry while connecting or during an active flash.
    if (s.status === "connecting" || s.status === "running") return;
    this._patch(sessionId, { status: "connecting", error: undefined });
    this._pushLog(sessionId, "Đang kết nối...");
    try {
      await s.service.connectAuthorizedDevice(s.usbDevice);
      const info = await s.service.getDeviceInfo();
      this._patch(sessionId, { status: "ready", info });
      this._pushLog(sessionId, `Đã kết nối · ${info.protocolVersion ?? "OK"}`);
      if (this._autoIdentify) await this._runAutoIdentify(s);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this._patch(sessionId, { status: "failed", error: message });
      this._pushLog(sessionId, `Lỗi kết nối: ${message.slice(0, 200)}`);
    }
  }

  /** Auto-identify toggle: runs identify 7 (V2) / getvar identify (V3) right after connect. */
  setAutoIdentify(enabled: boolean): void {
    this._autoIdentify = enabled;
  }

  get autoIdentify(): boolean {
    return this._autoIdentify;
  }

  private async _runAutoIdentify(s: MutableSession): Promise<void> {
    const legacy = s.generation === "legacy-v2";
    const command = legacy ? "identify 7" : "getvar identify";
    const ctx = { sessionId: s.id, deviceLabel: s.label };
    const started = operationLogger.start("AMLOGIC", "identify", `${s.label} · Đang tự động chạy ${command}`, ctx);
    try {
      const output = legacy ? await s.service.runLegacyCommand(command) : await s.service.runAdnlCommand(command);
      this._pushLog(s.id, `Identify · ${command}${output ? ` · ${output.slice(0, 200)}` : " · success"}`);
      operationLogger.success("AMLOGIC", "identify", started, `${s.label} · ${command}${output ? ` · ${output.slice(0, 4000)}` : " · success"}`, undefined, ctx);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this._pushLog(s.id, `Identify · lỗi · ${message.slice(0, 200)}`);
      operationLogger.failure("AMLOGIC", "identify", started, `${s.label} · ${message}`, undefined, ctx);
    }
  }

  async disconnectSession(sessionId: string): Promise<void> {
    const s = this._sessions.get(sessionId);
    if (!s) return;
    try {
      await s.service.disconnect();
    } catch { /* ignore */ }
    if (s.usbDevice) this._deviceMap.delete(s.usbDevice);
    this._patch(sessionId, {
      status: "disconnected",
      activeJobId: undefined,
      progress: undefined,
    });
    this._pushLog(sessionId, "Đã ngắt kết nối.");
  }

  removeSession(sessionId: string): void {
    const s = this._sessions.get(sessionId);
    if (!s) return;
    if (s.usbDevice) this._deviceMap.delete(s.usbDevice);
    void s.service.disconnect().catch(() => undefined);
    this._sessions.delete(sessionId);
    this._notify();
  }

  getSession(sessionId: string): AmlogicDeviceSession | undefined {
    return this._sessions.get(sessionId) as AmlogicDeviceSession | undefined;
  }

  // ── Called by AmlogicPage job runners ────────────────────────────────────

  /**
   * `epoch`, when provided, must match the session's current `connectionEpoch`.
   * A mismatch means the calling job belongs to a re-enumeration that has
   * since moved on (a stale callback); it is silently ignored.
   */
  private _epochStale(sessionId: string, epoch?: number): boolean {
    if (epoch === undefined) return false;
    const s = this._sessions.get(sessionId);
    return !s || s.connectionEpoch !== epoch;
  }

  updateSessionProgress(sessionId: string, progress: OperationProgress | undefined, epoch?: number): void {
    if (this._epochStale(sessionId, epoch)) return;
    this._patch(sessionId, { progress });
  }

  pushSessionLog(sessionId: string, message: string, epoch?: number, context?: { batchId?: string; jobId?: string }): void {
    if (this._epochStale(sessionId, epoch)) return;
    this._pushLog(sessionId, message, context);
  }

  /** Keeps one live progress row per batch job instead of logging every data chunk. */
  upsertSessionProgressLog(sessionId: string, progress: OperationProgress, epoch: number | undefined, context: { batchId: string; jobId: string }): void {
    if (this._epochStale(sessionId, epoch)) return;
    const session = this._sessions.get(sessionId);
    if (!session) return;
    const percent = progress.totalBytes && progress.totalBytes > 0
      ? Math.min(100, Math.max(0, progress.completedBytes / progress.totalBytes * 100))
      : undefined;
    const entry: SessionLogEntry = {
      timestamp: new Date().toISOString(),
      message: percent === undefined ? progress.phase : `${progress.phase} · ${percent.toFixed(1)}%`,
      batchId: context.batchId,
      jobId: context.jobId,
      kind: "progress",
      progressPercent: percent,
    };
    let existingIndex = -1;
    for (let index = session.logs.length - 1; index >= 0; index -= 1) {
      const current = session.logs[index];
      if (current.kind === "progress" && current.batchId === context.batchId && current.jobId === context.jobId) {
        existingIndex = index;
        break;
      }
    }
    if (existingIndex >= 0) {
      const logs = [...session.logs];
      logs[existingIndex] = entry;
      session.logs = logs;
    } else {
      session.logs = [...session.logs.slice(-99), entry];
    }
    this._notify();
  }

  /**
   * Update session lifecycle status during a job.
   * Terminal statuses (completed / failed / cancelled) also clear progress/activeJobId.
   */
  setSessionJobStatus(
    sessionId: string,
    status: AmlogicSessionStatus,
    jobId?: string,
    error?: string,
    epoch?: number,
  ): void {
    if (this._epochStale(sessionId, epoch)) return;
    // A late job callback must not hide the physical USB state after unplug.
    // Reconnection uses _connectSession/_patch and is therefore unaffected.
    if (this._sessions.get(sessionId)?.status === "disconnected" && status !== "disconnected") return;
    const terminal = status === "completed" || status === "failed" || status === "cancelled";
    const patch: Partial<MutableSession> = { status };
    if (terminal) {
      patch.activeJobId = undefined;
      patch.progress = undefined;
    } else if (jobId !== undefined) {
      patch.activeJobId = jobId;
    }
    if (error !== undefined) patch.error = error;
    this._patch(sessionId, patch);
  }
}
